<template>
  <cv-accordion-item v-show="showTagging" :open="open" class="resource-tagging-view-item">
    <template #title>
      <span class="title">{{ headerTitle }}</span>
    </template>
    <template #content>
      <div class="form-group tag-content">
        <div v-show="showSubject || showLevelAndStreams" class="field-group subject-level">
          <!-- subject -->
          <dl v-show="showSubject" class="field-set subject">
            <dt class="field-label">{{ t("resource_tagging_label_subject") }}</dt>
            <dd class="field-value">
              <div class="output-text">
                <p>{{ subjectNames }}</p>
              </div>
            </dd>
          </dl>

          <!-- level and course -->
          <dl v-show="showLevelAndStreams" class="field-set level-stream">
            <dt class="field-label">
              {{ t("resource_tagging_label_level_and_course") }}
            </dt>
            <dd class="field-value">
              <div class="output-text">
                <p>{{ levelAndStreams }}</p>
              </div>
            </dd>
          </dl>
        </div>

        <!-- syllabus -->
        <dl v-show="showSyllabus" class="field-set syllabus">
          <dt class="field-label">{{ t("resource_tagging_label_syllabus") }}</dt>
          <dd class="field-value">
            <div class="output-text">
              <p>{{ syllabusLabel }}</p>
            </div>
          </dd>
        </dl>

        <!-- cognitive level -->
        <dl v-if="showCognitiveLevel" class="field-set cognitive-level">
          <dt class="field-label">{{ t("resource_tagging_label_cognitive_level") }}</dt>
          <dd v-if="haveCognitiveLevel" class="field-value">
            <ul>
              <li>
                <wiris-rich-text :content="cognitiveLevelTag.cognitiveLevelText" />
              </li>
            </ul>
          </dd>
          <dd v-else class="field-value">-</dd>
        </dl>
        <!-- topic/sub-topics -->
        <dl class="field-set topic">
          <dt class="field-label">{{ t("resource_tagging_label_subtopic") }}</dt>
          <div v-if="showLoNameToggle" class="lo-wrapper">
            <!--  Display learning outcome toggle -->
            <cv-toggle
              class="toggle"
              value="view-display-learning-outcome-toggle"
              :label="loNameToggleLabel"
              :checked="showLoName"
              small
              @change="toggleLoName"
            >
              <template #text-left>{{ loNameToggleLabel }}</template>
              <template #text-right>{{ loNameToggleLabel }}</template>
            </cv-toggle>
            <!--  Display learning outcome tooltip  -->
            <cv-tooltip
              direction="bottom"
              :alignment="loNameToggleTooltipAlignment"
              :tip="loNameToggleTooltip"
              class="field-tooltip"
            >
              <svg-icon name="Info24" kind="action" />
            </cv-tooltip>
          </div>
          <dd v-if="!isResourceTaggingsTopicEmpty" class="field-value">
            <ul>
              <li v-for="tagging in resourceTaggingTopics" :key="tagging.id">
                <div v-if="showLoNameOnly">
                  <wiris-rich-text :content="getNameOrText(tagging)" />
                </div>
                <div v-else>
                  <wiris-rich-text :content="tagging.text" />
                  <span v-if="showLoName" class="lo-display-name"> {{ tagging.name }} </span>
                </div>
              </li>
            </ul>
          </dd>
          <dd v-else class="field-value">-</dd>
        </dl>
      </div>
    </template>
  </cv-accordion-item>
</template>

<script setup lang="ts">
import { isDefined, isEmpty, isNull } from "@/utils/var.check";
import { NAMESPACE, RESOURCE_TAGGING_RESOURCE_TYPE } from "@modules/editor/service/editor.constant";
import ROLE from "@configs/role";
import taggingStore from "@modules/resource-tagging-selection/components/ResourceTaggingSelectionStore";
import { useLearningOutcomeToggle } from "@modules/content-map/components/composables/learning-outcome-toggle.ts";
import DeviceUtils from "@utils/device.utils.ts";
import { ref, computed, watch } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { useErrorHandler } from "@/plugins/common/composables/error";

interface Props {
  open?: boolean;
  resourceTagging?: {
    contentMapMetadata: unknown;
    [key: string]: unknown;
  } | null;
  resourceType: string;
  subjectLevelAndStreamContentMapData?: unknown[];
  hideEmptyIndicator?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  open: false,
  resourceTagging: null,
  subjectLevelAndStreamContentMapData: () => [],
  hideEmptyIndicator: false,
});

const { t } = useI18n();
const { passiveApiFailHandler } = useErrorHandler();

// Data
const showTagging = ref(true);
const isContentMapReady = ref(false);
const hasAtLeastOneLoWithName = ref(false);

const { showLoName, toggleLoName, loNameToggleLabel, loNameToggleTooltip, showLoNameOnly } =
  useLearningOutcomeToggle();

const store = useStore();

// Computed
const hasRole = computed(() => store.getters[`${NAMESPACE.AUTH}/hasRole`]);

const isStudent = computed(() => store.getters[`${NAMESPACE.AUTH}/isStudent`]);

const showLoNameToggle = computed(() => {
  if (isStudent.value) {
    return false;
  } else {
    return hasAtLeastOneLoWithName.value;
  }
});

const loNameToggleTooltipAlignment = computed(() => {
  return DeviceUtils.isMobile() ? "end" : "center";
});

const isContentApprover = computed(() => {
  return hasRole.value(ROLE.CONTENT_APPROVER);
});

const isContentOfficer = computed(() => {
  return hasRole.value(ROLE.CONTENT_OFFICER);
});

const contentMapMetadata = computed(() => {
  return props.resourceTagging ? props.resourceTagging.contentMapMetadata : {};
});

const contentMapTitle = computed(() => {
  return contentMapMetadata.value ? contentMapMetadata.value.title : "";
});

const subjectNames = computed(() => {
  if (!isContentMapReady.value) return;
  if (isEmpty(props.resourceTagging?.contentMapMetadata)) {
    return "-";
  }

  const contentMap = taggingStore.getContentMap(props.resourceTagging?.contentMapMetadata.uuid);
  if (isEmpty(contentMap)) {
    return "-";
  }
  const subjectNames: string[] = [];
  Object.keys(contentMap.subjects).forEach((subjectId) => {
    subjectNames.push(contentMap.subjects[subjectId].name);
  });

  return subjectNames.join(", ");
});

const taggingCount = computed(() => {
  return !isEmpty(resourceTaggingTopics.value) ? resourceTaggingTopics.value.length : 0;
});

const headerTitle = computed(() => {
  let headerTitle = "";
  switch (props.resourceType.toUpperCase()) {
    case RESOURCE_TAGGING_RESOURCE_TYPE.LESSON:
      headerTitle = t("resource_tagging_lesson_header_title");
      break;
    case RESOURCE_TAGGING_RESOURCE_TYPE.LESSON_ACTIVITY:
      headerTitle = t("resource_tagging_section_header_title");
      break;
    case RESOURCE_TAGGING_RESOURCE_TYPE.QUESTION:
      headerTitle = t("resource_tagging_question_header_title");
      break;
    case RESOURCE_TAGGING_RESOURCE_TYPE.RUBRIC:
    default:
      break;
  }

  if (isEmpty(props.resourceTagging)) {
    return headerTitle;
  }

  if (!isEmpty(syllabusLabel.value)) {
    return syllabusLabel.value + " - " + taggingCount.value + " selected";
  }

  return headerTitle;
});

const showSubject = computed(() => {
  return !isEmpty(subjectNames.value) && !props.hideEmptyIndicator;
});

const showLevelAndStreams = computed(() => {
  return !isEmpty(levelAndStreams.value) && !props.hideEmptyIndicator;
});

const showSyllabus = computed(() => {
  return !isEmpty(props.resourceTagging?.contentMapMetadata) && !props.hideEmptyIndicator;
});

const syllabusLabel = computed(() => {
  const syllabusName = contentMapMetadata.value ? contentMapMetadata.value.syllabus : "";
  let syllabusLabel = contentMapTitle.value;
  if (!isEmpty(syllabusName)) {
    syllabusLabel = syllabusLabel.concat(` - ${syllabusName}`);
  }

  return syllabusLabel;
});

const levelAndStreams = computed(() => {
  if (!isContentMapReady.value) return;
  if (isEmpty(props.resourceTagging?.contentMapMetadata)) {
    if (isEmpty(props.resourceTagging?.levelAndStreams)) {
      return "-";
    }

    const joinedLevelAndStreams: string[] = [];
    props.resourceTagging.levelAndStreams.forEach((levelAndStream) => {
      joinedLevelAndStreams.push(levelAndStream.levelAndStream.displayName);
    });
    return joinedLevelAndStreams.join(", ");
  }

  const contentMap = taggingStore.getContentMap(props.resourceTagging.contentMapMetadata.uuid);
  if (isEmpty(contentMap)) {
    return "-";
  }
  const levelAndStreamMap: Record<string, string> = {};
  Object.keys(contentMap.subjects).forEach((subjectId) => {
    const levelAndStreams = contentMap.subjects[subjectId].levelAndStreams;
    Object.keys(levelAndStreams).forEach((levelAndStreamId) => {
      if (!isDefined(levelAndStreamMap[levelAndStreamId])) {
        levelAndStreamMap[levelAndStreamId] = levelAndStreams[levelAndStreamId].name;
      }
    });
  });

  return Object.values(levelAndStreamMap).join(", ");
});

const resourceTaggingTopics = computed(() => {
  if (!isEmpty(props.resourceTagging) && !isEmpty(props.resourceTagging?.taggings)) {
    return props.resourceTagging?.taggings.filter((tagging) => {
      return !isDefined(tagging.cognitiveLevelId) || isNull(tagging.cognitiveLevelId);
    });
  }
  return [];
});

const cognitiveLevelTag = computed(() => {
  if (!isEmpty(props.resourceTagging) && !isEmpty(props.resourceTagging?.taggings)) {
    return props.resourceTagging?.taggings.find(
      (tagging) => isDefined(tagging.cognitiveLevelId) && !isNull(tagging.cognitiveLevelId),
    );
  }
  return [];
});

const showCognitiveLevel = computed(() => {
  return (
    props.resourceType.toUpperCase() === RESOURCE_TAGGING_RESOURCE_TYPE.QUESTION ||
    props.resourceType.toUpperCase() === RESOURCE_TAGGING_RESOURCE_TYPE.RUBRIC
  );
});

const haveCognitiveLevel = computed(() => {
  return isDefined(cognitiveLevelTag.value);
});

const isResourceTaggingsTopicEmpty = computed(() => {
  return resourceTaggingTopics.value.length === 0;
});

// Methods
const getNameOrText = function (tagging) {
  return tagging.name ?? tagging.text;
};

const setHasAtLeastOneLoWithName = function () {
  hasAtLeastOneLoWithName.value =
    props.resourceTagging?.taggings?.some((tagging) => tagging.name !== null) ?? false;
};

const hideResourceTaggingWithoutAccessRight = function () {
  if (!(isContentApprover.value || isContentOfficer.value)) {
    if (
      !isEmpty(props.resourceTagging) &&
      !isEmpty(props.resourceTagging.contentMapMetadata) &&
      !props.resourceTagging.contentMapMetadata.started &&
      !props.resourceTagging.contentMapMetadata.viewableByTeachers
    ) {
      showTagging.value = false;
    }
  }
};

// Watch
watch(
  () => props.subjectLevelAndStreamContentMapData,
  (value) => {
    isContentMapReady.value = false;
    taggingStore.processSubjectLevelAndStreamData(value, true).then(() => {
      isContentMapReady.value = true;
    }, passiveApiFailHandler);
  },
  { immediate: false },
);

// Created
// taggingStore
//   .processSubjectLevelAndStreamData(props.subjectLevelAndStreamContentMapData, true)
//   .then(() => {
//     isContentMapReady.value = true;
//   }, passiveApiFailHandler);
hideResourceTaggingWithoutAccessRight();
setHasAtLeastOneLoWithName();
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";
.resource-tagging-view-item {
  .field-set {
    &.topic {
      margin-bottom: 0;

      li {
        position: relative;
        padding-left: $spacing-05;
        margin-bottom: $spacing-05;
        line-height: 1.5rem; //prevent scrollbar for single line
        // Handle long Wiris images added in content map by displaying the horizontal scrollbar
        overflow-x: auto;
        overflow-y: hidden;

        &:last-child {
          margin-bottom: 0;
        }

        &::before {
          content: "";
          display: inline-block;
          width: 0.5rem;
          height: 0.5rem;
          position: absolute;
          left: 0;
          top: 0.4rem;
          border-radius: 50%;
          background-color: $text-03;
        }
      }

      .lo-display-name {
        @include carbon--type-style("label-01", true);
        color: $text-02;
        margin-top: $spacing-02;
      }
    }
  }

  .lo-wrapper {
    display: flex;
    align-items: center;
    padding: $spacing-03 0;
    gap: $spacing-02;
    .sls-icon {
      min-width: 24px;
    }
    .bx--toggle-input__label {
      @include carbon--type-style("body-short-01");
      display: flex;
      flex-flow: row-reverse;
      align-items: center;
      gap: $spacing-03;
      color: $text-02;
    }

    .toggle {
      max-width: fit-content;

      &.bx--toggle-input--small {
        & + .bx--toggle-input__label {
          .bx--toggle__switch {
            margin-top: 0;
          }
        }
      }

      .bx--toggle__text--off,
      .bx--toggle__text--on {
        display: none;
      }
    }
  }
}
</style>
